import {
  Break,
  BreakCode,
  BreakCodeResponse,
  CodeAvailability,
  spansMultipleCodeSets,
} from './break.model'

export interface BulkBreakGroup {
  breakType: string // stripped typeName; grouping key
  /** Friendly break-type name for display (Break.displayName); falls back to breakType. */
  displayName?: string
  breaks: Break[]
  availability: CodeAvailability // intersected across ALL accounts in the group
  existingCount: number // # breaks where isBreakCoded(b)
  totalCount: number // breaks.length
}

/**
 * Has this break been coded AT ALL — either field. Deliberately NOT "fully coded":
 * whether a specific field still needs a code is per field, via writeTargets.
 */
export function isBreakCoded(b: Break): boolean {
  return !!b.actionCode || !!b.descriptionCode
}

/**
 * Would Keep silently discard the chosen code for this break?
 *
 * Keep sends preserveExistingBreakCodes, which the backend applies PER FIELD, not per
 * break: a field already holding a value is preserved, an EMPTY one takes the chosen
 * code — so a break coded on action only still receives a chosen description code.
 * Both backends resolve the two fields independently (Helios `resolveOverlayCodes` in
 * BreaksCommandHandler.kt, Perseus `resolveBreakCode` in Issues.java).
 *
 * Only true when the chosen code would actually CHANGE something: both dialogs prefill
 * their code controls from the selected breaks, so counting a MATCHING code would fire
 * the warning on every open.
 */
export function keepWouldDropCode(
  b: Break,
  actionCode: string | null,
  descriptionCode: string | null,
): boolean {
  return (
    (!!actionCode && !!b.actionCode && b.actionCode !== actionCode) ||
    (!!descriptionCode &&
      !!b.descriptionCode &&
      b.descriptionCode !== descriptionCode)
  )
}

/**
 * A break is "noted" when it carries a non-empty note (any note — not necessarily
 * a valid 3-word one). Used to decide whether leaving the note field blank is a
 * safe no-op (Keep) across a selection, independent of whether existing notes match.
 */
export function isBreakNoted(b: Break): boolean {
  return !!(b.note ?? '').trim()
}

/**
 * Every break in the list already carries a note (presence only). Drives the
 * relaxation that lets an empty note be a safe no-op across a selection — shared by
 * the bulk Hold/Accept and Edit-Note validators so they agree on the edge cases.
 */
export function allBreaksNoted(breaks: Break[]): boolean {
  return breaks.length > 0 && breaks.every(isBreakNoted)
}

/** Group breaks by their type with the trailing severity suffix removed. */
export function stripBreakTypeSuffix(typeName: string): string {
  return (typeName ?? '').replace(/_(ERROR|FATAL)$/, '')
}

/**
 * Resolve the codes offered for a group of breaks.
 * - undefined breakCodes -> unavailable.
 * - breaks spanning both code sets (Enfusion + global) -> mixedCodeSets.
 * - otherwise: for ACTION and DESCRIPTION separately, take the codes each
 *   account offers for EVERY group type, then intersect by code string across
 *   all accounts (keeping the BreakCode object from the first account). A group
 *   spanning accounts only offers codes ALL its accounts support.
 */
export function resolveGroupCodes(
  breaks: Break[],
  breakCodes: BreakCodeResponse | undefined,
): CodeAvailability {
  if (!breakCodes) {
    return { status: 'unavailable' }
  }
  if (breaks.length === 0) {
    return { status: 'ready', actionCodes: [], descriptionCodes: [] }
  }

  if (spansMultipleCodeSets(breaks)) {
    return { status: 'mixedCodeSets' }
  }

  const groupTypes = [
    ...new Set(breaks.map(b => stripBreakTypeSuffix(b.typeName))),
  ]
  const accountIds = [...new Set(breaks.map(b => b.acctId))]
  const matchesAllGroupTypes = (c: BreakCode): boolean =>
    groupTypes.every(gt => (c.breakTypes ?? []).includes(gt))

  const intersect = (codeType: 'ACTION' | 'DESCRIPTION'): BreakCode[] => {
    const perAccount = accountIds.map(acctId =>
      (breakCodes[acctId]?.[codeType] ?? []).filter(matchesAllGroupTypes),
    )
    const [first, ...rest] = perAccount
    if (!first) {
      return []
    }
    return first.filter(c =>
      rest.every(set => set.some(o => o.code === c.code)),
    )
  }

  return {
    status: 'ready',
    actionCodes: intersect('ACTION'),
    descriptionCodes: intersect('DESCRIPTION'),
  }
}

/**
 * Group breaks by suffix-stripped type. Each group carries its intersected
 * code availability, the count of already-coded breaks, and its total size.
 * Groups are returned sorted by breakType.
 */
export function buildBreakGroups(
  breaks: Break[],
  breakCodes: BreakCodeResponse | undefined,
): BulkBreakGroup[] {
  const byType = new Map<string, Break[]>()
  for (const b of breaks) {
    const key = stripBreakTypeSuffix(b.typeName)
    const bucket = byType.get(key)
    if (bucket) {
      bucket.push(b)
    } else {
      byType.set(key, [b])
    }
  }

  return [...byType.entries()]
    .map(([breakType, groupBreaks]) => ({
      breakType,
      displayName: groupBreaks[0]?.displayName
        ? String(groupBreaks[0].displayName)
        : breakType,
      breaks: groupBreaks,
      availability: resolveGroupCodes(groupBreaks, breakCodes),
      existingCount: groupBreaks.filter(isBreakCoded).length,
      totalCount: groupBreaks.length,
    }))
    .sort((a, b) => a.breakType.localeCompare(b.breakType))
}
